import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import BuildPicker from "./BuildPicker";
import { atLeast, ApiError, api, type Bench, type BenchCurrentImage, type BenchImage, type LaunchOptions, type TestSelection } from "../api";
import { Loading } from "./Look";

/** The service's defaults, and the catalogue keys that name them. Used when a
 *  project has not set its own list - or when the server is older than the
 *  per-project list and does not say. The VALUE is what pytest is given and is
 *  never translated. */
const DEFAULT_LABELS: Record<string, "runPanel.markers.auto" | "runPanel.markers.screenshot" | "runPanel.markers.both"> = {
  "auto": "runPanel.markers.auto",
  "screenshot_partial": "runPanel.markers.screenshot",
  "auto or screenshot_partial": "runPanel.markers.both",
};
const DEFAULTS: TestSelection[] = Object.keys(DEFAULT_LABELS).map((marker) => ({
  marker,
  label: marker,
}));

type DefaultKey = (typeof DEFAULT_LABELS)[string];

/**
 * The list the form offers, and what to call each entry.
 *
 * TWO SOURCES OF A LABEL, and the flag says which applies. While a project uses
 * the service's defaults, the label comes from the CATALOGUE, so it reads in the
 * reader's language. Once a project has written its own list, the label is the
 * project's DATA and is shown as written - one language for every reader, like
 * a launch variable's label. The flag rather than a guess, because a project may
 * well define an entry whose marker happens to be `auto`, and its own wording
 * for it must not be overwritten by ours.
 */
export function selectionsFor(bench: Bench): { marker: string; label: string; key?: DefaultKey }[] {
  const list = bench.test_selections?.length ? bench.test_selections : DEFAULTS;
  const useCatalogue = bench.test_selections_default !== false;
  return list.map((s) => ({
    marker: s.marker,
    label: s.label,
    key: useCatalogue ? DEFAULT_LABELS[s.marker] : undefined,
  }));
}

/** The "what to test" value that means: every profile, as one campaign. */
const EVERYTHING = "__every_profile__";

// max-w-full: a select is as wide as its longest option, and "Every profile: 4
// runs, 53 cases" was wider than the card on a phone.
const FIELD =
  "max-w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-800";

/**
 * Start testing: the ONE form for it, wherever somebody asks.
 *
 * Clear-screens stop B (web/STYLE_GUIDE.md). There used to be a run form on the
 * bench and a different one on the project, and the Now page sent people to
 * the bench to find the first. The same choices in the same order now, from
 * all three: which bench, which build, what to test, why. The bench is fixed
 * where the page already is one; the project page and Now offer a list.
 *
 * Starting a job still moves hardware, so the rules the run form set stay:
 * say what it costs before it happens, offer lists rather than free text where
 * a list exists, and do not offer what the server would refuse.
 */
export default function StartTesting({
  benches,
  projectId,
  benchId,
  fromProject = false,
  confirm = true,
  onQueued,
}: {
  /** The benches to choose from. Without it the list is loaded, of the
   *  project when one is given. */
  benches?: Bench[];
  projectId?: number;
  /** The bench to start on, when the reader came from one. */
  benchId?: number;
  /** On the project page itself, so no pointer back to the project. */
  fromProject?: boolean;
  /** Say "queued" under the form. Off where the page lists the queue anyway. */
  confirm?: boolean;
  onQueued?: () => void;
}) {
  const { t } = useTranslation();
  const [fetched, setFetched] = useState<Bench[] | null>(null);
  const [error, setError] = useState("");
  const [chosen, setChosen] = useState<number | undefined>(benchId);

  useEffect(() => {
    if (benches) return;
    let alive = true;
    api
      .benches(projectId)
      .then((d) => alive && setFetched(d.benches))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [benches, projectId]);

  const list = benches ?? fetched;
  if (error) {
    return (
      <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
        {error}
      </p>
    );
  }
  if (!list) return <Loading label={t("start.loading")} />;
  if (list.length === 0) return <p className="text-sm text-slate-500">{t("start.none")}</p>;

  const ready = (b: Bench) => b.online && !b.lease && atLeast(b.role, "operator");
  const bench = list.find((b) => b.id === chosen) ?? list.find(ready) ?? list[0];
  // Why a bench cannot take a job, in the list itself: choosing one and only
  // then finding the button greyed out is a question asked one step too late.
  const label = (b: Bench) =>
    b.lease
      ? t("start.benchHeld", { bench: b.name, actor: b.lease.actor })
      : !b.online
        ? t("start.benchOffline", { bench: b.name })
        : !atLeast(b.role, "operator")
          ? t("start.benchReadOnly", { bench: b.name })
          : b.name;

  return (
    <div className="space-y-3">
      {list.length > 1 && (
        <label className="block text-sm">
          <span className="mb-1 block text-slate-500 sm:mb-0 sm:mr-2 sm:inline">{t("start.bench")}</span>
          <select
            aria-label={t("start.bench")}
            className={FIELD}
            value={bench.id}
            onChange={(e) => setChosen(Number(e.target.value))}
          >
            {list.map((b) => (
              <option key={b.id} value={b.id}>
                {label(b)}
              </option>
            ))}
          </select>
        </label>
      )}
      {/* Keyed by bench: a profile, a build or a filter chosen for one bench
          means nothing on the next, so switching starts the form afresh. */}
      <BenchForm
        key={bench.id}
        bench={bench}
        fromProject={fromProject}
        confirm={confirm}
        onQueued={onQueued}
      />
    </div>
  );
}

function BenchForm({
  bench,
  fromProject,
  confirm,
  onQueued,
}: {
  bench: Bench;
  fromProject: boolean;
  confirm: boolean;
  onQueued?: () => void;
}) {
  const { t } = useTranslation();
  // Queueing work is the same rung as taking the lease: both decide what the
  // hardware does next.
  const mayOperate = atLeast(bench.role, "operator");
  const choices = selectionsFor(bench);
  // The first entry, not "auto": a project's own list need not contain it.
  const [marker, setMarker] = useState(choices[0]?.marker ?? "auto");
  const [filter, setFilter] = useState("");
  const [reason, setReason] = useState("");
  const [buildRef, setBuildRef] = useState("");
  const [buildOptions, setBuildOptions] = useState<Record<string, LaunchOptions>>({});
  const [images, setImages] = useState<Record<string, BenchImage>>({});
  const [current, setCurrent] = useState<BenchCurrentImage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [queued, setQueued] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);

  // What the AGENT will do on request, not what the rig turned out to have on
  // its last run. `caps` is an observation; this is a capability, and only it
  // decides whether a version may be named.
  const canBuild = (bench.agent_caps ?? []).includes("build");
  // WHAT TO TEST, where the bench's product names execution profiles. A
  // profile is the recipe the rig is prepared with, and a product whose cases
  // belong to profiles skips every one of them without it - so the form asks
  // for a profile first and folds the marker away with the filter.
  const profiles = (bench.agent_caps ?? []).includes("profiles") ? (bench.profiles ?? []) : [];
  const preparable = profiles.filter((p) => !p.problem);
  // EVERY PROFILE FIRST where the bench can prepare more than one: a build
  // needs the whole regression, and one profile is the exception (piece 4).
  // The choice says in its own words how many runs it starts.
  const [profile, setProfile] = useState(
    () => (preparable.length > 1 ? EVERYTHING : (preparable[0]?.id ?? profiles[0]?.id ?? "")),
  );
  const everything = profile === EVERYTHING;

  useEffect(() => {
    // After the form, and only where the field exists. Reaches a registry, so
    // it is slow and fails on its own; any failure is simply "no list", which
    // the field already handles.
    if (!canBuild) return;
    let alive = true;
    api
      .buildOptions(bench.id)
      .then((d) => {
        if (!alive) return;
        setBuildOptions(d.options ?? {});
        setImages(d.images ?? {});
        setCurrent(d.current ?? null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [bench.id, canBuild]);

  if (bench.lease) {
    return (
      <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        {/* Two keys rather than one with an optional tail: Polish and
            Finnish put the purpose in a different place in the sentence, and
            a fragment glued on after the fact cannot move with it. */}
        <Trans
          i18nKey={bench.lease.purpose ? "runPanel.leaseHeldFor" : "runPanel.leaseHeld"}
          values={{ actor: bench.lease.actor, purpose: bench.lease.purpose }}
          components={{ b: <b /> }}
        />
      </p>
    );
  }

  async function queue() {
    setBusy(true);
    setError("");
    setQueued(false);
    setCampaignId(null);
    try {
      if (everything) {
        const r = await api.startCampaign(bench.id, reason.trim(), canBuild ? buildRef.trim() : "");
        setReason("");
        setCampaignId(r.campaign_id);
        onQueued?.();
        return;
      }
      if (profiles.length) {
        await api.queueJob(bench.id, marker, filter.trim(), reason.trim(),
                           canBuild ? buildRef.trim() : "", profile);
      } else {
        await api.queueJob(bench.id, marker, filter.trim(), reason.trim(),
                           canBuild ? buildRef.trim() : "");
      }
      setReason("");
      setQueued(true);
      onQueued?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // The same field under two names: the whole question when a bench has no
  // profiles ("What to test", as everywhere else), a narrower one under Advanced.
  const testsField = (label: string) => (
    <label className="text-sm">
      <span className="mb-1 block text-slate-500 sm:mb-0 sm:mr-2 sm:inline">{label}</span>
      <select className={FIELD} value={marker} onChange={(e) => setMarker(e.target.value)}>
        {choices.map((m) => (
          <option key={m.marker} value={m.marker}>
            {m.key ? t(m.key) : m.label}
          </option>
        ))}
      </select>
    </label>
  );
  const chosenProfile = profiles.find((p) => p.id === profile);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      {profiles.length > 0 ? (
        <label className="block text-sm">
          <span className="mb-1 block text-slate-500 sm:mb-0 sm:mr-2 sm:inline">{t("runPanel.profile")}</span>
          <select
            aria-label={t("runPanel.profile")}
            className={FIELD}
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
          >
            {preparable.length > 1 && (
              <option value={EVERYTHING}>
                {t("start.everyProfile", {
                  count: preparable.length,
                  cases: preparable.reduce((n, p) => n + (p.cases ?? 0), 0),
                })}
              </option>
            )}
            {profiles.map((p) => (
              <option key={p.id} value={p.id} disabled={Boolean(p.problem)}>
                {p.label ?? p.id}
                {p.cases != null ? ` (${t("runPanel.profileCases", { count: p.cases })})` : ""}
              </option>
            ))}
          </select>
          {chosenProfile?.problem && (
            <span className="mt-1 block text-xs text-amber-800 dark:text-amber-300">
              {chosenProfile.problem}
            </span>
          )}
        </label>
      ) : (
        testsField(t("runPanel.profile"))
      )}

      {/* ADVANCED, folded: picking tests by marker or by name is for the rare
          case somebody knows exactly which ones, and "-k" is not a word a
          tester should need to start a test. */}
      <details>
        <summary className="cursor-pointer text-xs text-slate-500">{t("runPanel.moreOptions")}</summary>
        <div className="mt-2 flex flex-wrap gap-2">
          {profiles.length > 0 && testsField(t("runPanel.tests"))}
          {/* A campaign runs every case of every profile; a name filter on it
              would be a campaign that planned what it then did not run. */}
          {!everything && (
            <label className="text-sm">
              <span className="mb-1 block text-slate-500 sm:mb-0 sm:mr-2 sm:inline">{t("runPanel.onlyMatching")}</span>
              <input
                className={FIELD}
                placeholder={t("runPanel.filterPlaceholder")}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </label>
          )}
        </div>
      </details>

      {/* OFFERED ONLY WHERE IT WORKS, and absent - not disabled - where it
          does not. A rig whose agent cannot install a build simply has no such
          choice. The server refuses a named build on such a bench with 409. */}
      {canBuild && (
        <>
          <BuildPicker
            options={buildOptions.build_ref}
            images={images}
            current={current}
            value={buildRef}
            onChange={setBuildRef}
          />
        </>
      )}

      <input
        aria-label={t("start.why")}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
        placeholder={t("runPanel.reasonPlaceholder")}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          disabled={busy || !bench.online || !mayOperate}
          onClick={queue}
        >
          {busy ? t("runPanel.queueing") : t("runPanel.start")}
        </button>
        <span className="text-xs text-slate-500">
          {!mayOperate
            ? t("runPanel.needOperator")
            : bench.online
              ? everything
                ? t("start.everyProfileCost")
                : t("runPanel.takesDut")
              : t("runPanel.offline")}
        </span>
      </div>

      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          {error}
        </p>
      )}
      {campaignId !== null && (
        <p className="rounded-md border border-success-300 bg-success-50 px-3 py-2 text-sm text-success-900 dark:border-success-900 dark:bg-success-950 dark:text-success-200">
          <Trans
            i18nKey="start.campaignStarted"
            components={{ campaignLink: <Link className="underline" to={`/campaigns/${campaignId}`} /> }}
          />
        </p>
      )}
      {queued && confirm && (
        <p className="rounded-md border border-success-300 bg-success-50 px-3 py-2 text-sm text-success-900 dark:border-success-900 dark:bg-success-950 dark:text-success-200">
          <Trans
            i18nKey="start.queued"
            values={{ bench: bench.name }}
            components={{ benchLink: <Link className="underline" to={`/benches/${bench.id}`} /> }}
          />
        </p>
      )}

      {/* WHICH VERSION THIS TESTS, said because the other way to start - a
          build from the project - chooses a version and lets CI pick the
          bench. Reported by Darek, who could not work out which to use. */}
      {!fromProject && !canBuild && (
        <p className="text-xs text-slate-500">
          {bench.project_id ? (
            <Trans
              i18nKey="runPanel.installedVersion"
              components={{
                projectLink: <Link className="underline" to={`/projects/${bench.project_id}`} />,
              }}
            />
          ) : (
            t("runPanel.installedVersionNoProject")
          )}
        </p>
      )}
    </div>
  );
}
